import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFeature } from './useFeature';

import { isDesktop } from 'helpers/ClientUtils';
import timedFetch from 'middleware/timedFetch';
import { trackError } from 'helpers/ErrorUtils';
import {
  getSponsoredAds,
  RequestSponsoredAdsBody,
  RequestSponsoredAdsBodyFilters,
  SponsoredAdsResponse,
  SponsoredAdsResults
} from 'apis/sponsoredAds';
import useMartyContext from 'hooks/useMartyContext';
import { engineAdsPriorityOrder, ENGINES, FEATURE_DOVE_ADS, FEATURE_MSA_ADS, FEATURE_PIQ_ADS } from 'constants/sponsoredAds';

export const trackSponsoredAdImpressions = ({ url }: { url: string }, fetcher = timedFetch) => {
  const fetch = fetcher('SponsoredAdImpressions');
  fetch(url).catch((error: unknown) => trackError('ERROR', 'Failed to send MSFT impression events', error));
};

function concatIfExists(keyword = '', val?: any) {
  if (val) {
    //purposeful whitespace.
    return `${keyword} ${val}`;
  }
  return keyword;
}

// builds the keyword for search implementation based off filters.
// keyword should be "gender + most specific zc + brand[0] + color[0]"
export function buildKeywordFromFilters(filters: Partial<RequestSponsoredAdsBodyFilters>) {
  //find most specific zc- 'zc1', 'zc2', etc.
  const mostSpecificZcKey = Object.keys(filters).reduce((val, current) => {
    if (current.includes('zc')) {
      //ex: zc2 > zc1
      return current > val ? current : val;
    }

    return val;
  }, '') as keyof RequestSponsoredAdsBodyFilters;

  //all of these are optional on the filters object, if they exist add it to the keyword.
  let keyword = filters.txAttrFacet_Gender?.[0] || '';
  keyword = concatIfExists(keyword, filters[mostSpecificZcKey]?.[0]);
  keyword = concatIfExists(keyword, filters.brandNameFacet?.[0]);
  keyword = concatIfExists(keyword, filters.colorFacet?.[0]);
  return keyword;
}

//configure the payload for the get request to sponsoredAds endpoint
//handle the response and return the results.
export function fetchSponsoredAds(
  args: Omit<RequestSponsoredAdsBody, 'isMobile'>,
  controller: AbortController,
  trackAdImpression = trackSponsoredAdImpressions
): Promise<SponsoredAdsResponse> {
  const body = {
    isMobile: !isDesktop(),
    ...args
  };

  return getSponsoredAds(body, controller).then((results: SponsoredAdsResponse) => {
    (args.engines || []).map(engine => {
      const { impressionUrl } = results[engine] || {};

      if (impressionUrl) {
        trackAdImpression({ url: impressionUrl });
      }
    });

    return results;
  });
}

type UseSponsoredAds = Pick<RequestSponsoredAdsBody, 'pageType'> & Partial<Pick<RequestSponsoredAdsBody, 'keywords' | 'minRes' | 'maxRes'>>;

//usage in PDP
type UseSponsoredAdsWithStockId = UseSponsoredAds & Required<Pick<RequestSponsoredAdsBody, 'stockId' | 'keywords'>>;

//usage in Search
type UseSponsoredAdsWithFilters = UseSponsoredAds &
  Required<Pick<RequestSponsoredAdsBody, 'filters'>> &
  Pick<RequestSponsoredAdsBody, 'placementId' | 'term'>;

function isUseSponsoredAdsWithFilters(args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters): args is UseSponsoredAdsWithFilters {
  return (args as UseSponsoredAdsWithFilters).filters !== undefined;
}

export const useSponsoredAds = (args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters, fetchAds = fetchSponsoredAds) => {
  const [sponsoredAds, setSponsoredAds] = useState<SponsoredAdsResults>();
  const sessionId = useSelector(state => state.cookies?.['session-id']);
  const { title: titleTag, canonical: url } = useSelector(state => state.meta?.documentMeta ?? {}); //documentMeta can be null
  const {
    marketplace: {
      search: { hasSponsoredAds }
    }
  } = useMartyContext();

  const engines: string[] = [];

  if (useFeature(FEATURE_DOVE_ADS)) {
    engines.push(ENGINES.DOVE);
  }

  if (useFeature(FEATURE_PIQ_ADS)) {
    engines.push(ENGINES.PIQ);
  }

  if (useFeature(FEATURE_MSA_ADS)) {
    engines.push(ENGINES.MSA);
  }

  // require all pieces of information before fetching sponsoredAds.
  useEffect(() => {
    // early return if no marketplace support or missing required pieces from state that are async changes via other actions.
    if (!hasSponsoredAds || !sessionId || !args.pageType || !titleTag || !url || engines.length === 0) return;

    // we need either or of these so if they dont exist, return;
    if (!(args as UseSponsoredAdsWithStockId).stockId && !(args as UseSponsoredAdsWithFilters).filters) return;

    const payload = {
      sessionId,
      titleTag,
      url,
      minRes: 5,
      maxRes: 15,
      engines,
      ...args
    } as RequestSponsoredAdsBody;

    if (isUseSponsoredAdsWithFilters(args)) {
      payload.keywords = [buildKeywordFromFilters(args.filters)];
    }

    const controller = new AbortController();
    fetchAds(payload, controller).then(data => {
      let adData: SponsoredAdsResults | undefined;

      engineAdsPriorityOrder.forEach(engine => {
        if (adData?.results && adData?.results?.length !== 0) {
          return;
        }

        adData = data[engine];

        if (adData?.results && adData.results.length > payload.maxRes) {
          adData.results = [...adData.results.slice(0, payload.maxRes)];
        }
      });

      setSponsoredAds(adData);
    });

    return () => {
      controller.abort();
    };
  }, [sessionId, titleTag, url, JSON.stringify(args)]);

  return sponsoredAds;
};
